import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getSaSesSettings,
  SaSesSettings,
} from "../../../helpers/super_admin_ops_helper";

const CHECKLIST_KEYS = [
  "iam",
  "configSet",
  "snsTopic",
  "eventDestination",
  "httpsSubscription",
  "dualRegion",
] as const;

/**
 * Runbook estático SES/SNS no SA (LAC-043).
 * Só ops — tenant não configura webhook/região.
 */
const SuperAdminSesSetupGuide = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<SaSesSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  document.title = t("sa.ses.guide.documentTitle");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getSaSesSettings();
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setError(t("sa.ses.error.load"));
          return;
        }
        setSettings(res.data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || t("sa.ses.error.load"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.ses.guide.title")}
          pageTitle={t("sa.menu.ops")}
        />
        <ToastContainer closeButton={false} limit={2} />

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            color="primary"
            size="sm"
            tag={Link}
            to="/super-admin/ses/endpoints"
          >
            {t("sa.ses.link.endpoints")}
          </Button>
          <Button
            color="soft-primary"
            size="sm"
            tag={Link}
            to="/super-admin/ses/byo-guide"
          >
            {t("sa.menu.sesByoGuide")}
          </Button>
        </div>

        {error && <Alert color="danger">{error}</Alert>}

        <Row>
          <Col lg={8}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">{t("sa.ses.guide.checklistTitle")}</h5>
              </CardHeader>
              <CardBody>
                <p className="text-muted">{t("sa.ses.guide.intro")}</p>
                <ol className="mb-0 ps-3">
                  {CHECKLIST_KEYS.map((key) => (
                    <li key={key} className="mb-3">
                      <strong>{t(`sa.ses.guide.steps.${key}.title`)}</strong>
                      <p className="text-muted mb-0 mt-1">
                        {t(`sa.ses.guide.steps.${key}.body`)}
                      </p>
                    </li>
                  ))}
                </ol>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("sa.ses.settings.webhookUrl")}
                </h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : (
                  <>
                    <Label for="sa-guide-webhook" className="visually-hidden">
                      {t("sa.ses.settings.webhookUrl")}
                    </Label>
                    <Input
                      id="sa-guide-webhook"
                      type="text"
                      readOnly
                      value={settings?.webhook_url || ""}
                      onFocus={(e) => e.target.select()}
                    />
                    <p className="text-muted small mb-0 mt-2">
                      {t("sa.ses.settings.webhookHint")}
                    </p>
                  </>
                )}
              </CardBody>
            </Card>
            <Alert color="info" className="mb-0">
              {t("sa.ses.guide.dualRegionNote")}
            </Alert>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SuperAdminSesSetupGuide;
